// Public privacy policy. URL: https://jarvis.yourproject.ai/privacy
// Written specifically to satisfy both Meta App Review for the Marketing API
// AND Google OAuth verification for Gmail (restricted scope) — both reviewers
// look for explicit data-handling language for their respective platforms.
// Not a substitute for a lawyer; review with counsel before production launch.

import { LegalLayout, Section } from './LegalLayout'
import Seo from '@/components/Seo'

export function PrivacyPolicy() {
  return (
    <>
    <Seo title="Privacy Policy — Jarvis" description="How Jarvis handles and protects your business data." path="/privacy" />
    <LegalLayout
      title="Privacy Policy"
      subtitle="How Jarvis collects, uses, stores, and deletes your data."
      effectiveDate="May 18, 2026"
    >
      <p>
        This Privacy Policy describes how <strong>ClearFinOps LLC</strong> (&ldquo;ClearFinOps,&rdquo;
        &ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;), based in Lehi, Utah, USA, collects,
        uses, stores, and shares information when you use Jarvis — the AI chief-of-staff service
        offered under the yourproject.ai brand (the &ldquo;Service&rdquo;).
      </p>
      <p>
        By signing up for or using the Service you agree to the practices described here. If you
        do not agree, please do not use the Service.
      </p>

      <Section heading="1. Who we are">
        <p>
          The Service is operated by ClearFinOps LLC, a Utah limited liability company. You can
          reach us at <a className="text-jarvis-blue hover:underline" href="mailto:kc@yourproject.ai">kc@yourproject.ai</a>.
        </p>
      </Section>

      <Section heading="2. Information we collect">
        <p>We collect three categories of information:</p>
        <p>
          <strong>(a) Account information you provide.</strong> When you create an account we
          receive your name, email address, and any company information you choose to enter.
          Authentication is handled through Microsoft Entra External ID; we do not see or store
          your password.
        </p>
        <p>
          <strong>(b) Information from third-party integrations you connect.</strong> When you
          choose to connect a third-party platform (such as Facebook Ads, Stripe, or Google),
          we receive only the data scopes you explicitly approve. Specifically for Facebook:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>The Facebook user ID and name of the person who authorizes the connection.</li>
          <li>
            The list of ad accounts you have access to, including their ID, name, currency,
            timezone, and status.
          </li>
          <li>
            For the ad accounts you choose to enable, the campaigns, ad sets, ads, and Marketing
            API Insights metrics those accounts produce — including spend, impressions, reach,
            frequency, clicks, click-through rate, CPC, CPM, conversion actions, video metrics,
            and similar advertising performance figures.
          </li>
          <li>An OAuth access token used to call the Facebook Marketing API on your behalf.</li>
        </ul>
        <p>
          We <em>do not</em> request or store post content, friend lists, message contents, or
          personal profile data beyond what is described above. We never request permissions
          we do not use.
        </p>
        <p>
          For Google (Gmail, Calendar, Drive, identity) — see Section 10 below for a detailed
          breakdown of which scopes we request, what data we access, what we store versus what we
          only read transiently, and our Limited Use commitments under the Google API Services
          User Data Policy.
        </p>
        <p>
          <strong>(c) Service-generated data.</strong> When you use Jarvis we generate analytical
          outputs — such as ad-performance scores, recommended actions, and morning briefings —
          and we log basic technical information (timestamps, request paths, error codes) needed
          to operate the Service.
        </p>
      </Section>

      <Section heading="3. How we use your information">
        <p>We use the information we collect to:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Provide, maintain, and improve the Service.</li>
          <li>
            Synchronize advertising performance data from connected platforms (such as the
            Facebook Marketing API) and score it for you using AI models.
          </li>
          <li>
            Generate written, voice, and email briefings about your business as you have configured.
          </li>
          <li>Process subscription billing through our payment processor (Stripe).</li>
          <li>Detect, prevent, and respond to security incidents and abuse.</li>
          <li>Comply with our legal obligations.</li>
        </ul>
        <p>
          We do <strong>not</strong> sell or rent your personal information, and we do not use
          Facebook data for advertising targeting outside the connected ad accounts themselves.
        </p>
      </Section>

      <Section heading="4. Profile enrichment (opt-in)">
        <p>
          If you choose to use the profile scan feature offered during onboarding (and available
          at any time from <em>Settings → Build my Jarvis profile</em>), we use the information
          you provide — including your email address, LinkedIn profile URL, and company website —
          to search third-party data providers (including{' '}
          <a className="text-jarvis-blue hover:underline" target="_blank" rel="noreferrer" href="https://www.apollo.io">Apollo.io</a>)
          for professional and business information about you. This may include your job title,
          company name, industry, headquarters, company size, technology stack, and a summary of
          your business. We also fetch your company&rsquo;s public homepage and use a large-language
          model to derive a short business profile from its content. This information is stored
          in your Jarvis profile to personalize the responses, briefings, and voice calls you receive.
          You can view, edit, re-scan, or delete this profile data from your account settings at
          any time. The profile scan is strictly opt-in — if you skip it, Jarvis continues to work
          using only the information you enter directly.
        </p>
      </Section>

      <Section heading="5. AI processing">
        <p>
          To score ad performance and produce briefings we send anonymized, aggregate performance
          metrics to large-language-model providers operating on our behalf, including Anthropic
          via Microsoft Azure AI Foundry. We send only the metrics and entity names needed for
          the analysis; we do not send your access tokens, personal-profile data, or contact lists.
          These providers act as data processors under contractual terms that prohibit them from
          using the inputs for their own training.
        </p>
      </Section>

      <Section heading="6. How we store and protect your data">
        <p>
          Your data is stored in Microsoft Azure data centers in the United States. Access tokens
          and other secrets are encrypted at rest using AES-256-GCM with a key held in Azure Key
          Vault; the encrypted material is then stored in Azure Cosmos DB. Network connections to
          our APIs are protected by TLS 1.2 or higher.
        </p>
        <p>
          No internet-connected service can be made perfectly secure. We work to protect your data
          but cannot guarantee its absolute security.
        </p>
      </Section>

      <Section heading="7. Data retention">
        <p>
          We retain your account and analytical data for as long as your account is active. If you
          disconnect a third-party integration (for example, by clicking Disconnect on the
          Facebook Ads integration card), we delete the encrypted access token for that integration
          immediately. Historical analytical snapshots derived from that integration are retained
          for up to ninety (90) days, after which they are purged. If you delete your Jarvis account
          we remove all personal data within thirty (30) days, except for limited records we are
          required to retain for tax, audit, or legal compliance.
        </p>
      </Section>

      <Section heading="8. Your rights">
        <p>
          You can access, correct, export, or delete most of your data directly from the in-product
          Settings page. You may also email{' '}
          <a className="text-jarvis-blue hover:underline" href="mailto:kc@yourproject.ai">kc@yourproject.ai</a>{' '}
          to:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Request a copy of the personal data we hold about you.</li>
          <li>Correct inaccurate information.</li>
          <li>Delete your account and the personal data associated with it.</li>
          <li>
            Withdraw a third-party authorization (for example, revoke Jarvis&rsquo;s access to a
            Facebook ad account).
          </li>
          <li>
            Object to or restrict certain processing (if you are located in the EU/UK or another
            jurisdiction that affords those rights).
          </li>
        </ul>
        <p>
          See also our{' '}
          <a className="text-jarvis-blue hover:underline" href="/data-deletion">Data Deletion Instructions</a>{' '}
          page.
        </p>
      </Section>

      <Section heading="9. Facebook-specific notes">
        <p>
          When you connect Facebook Ads, our use of information received from Meta will adhere to
          the{' '}
          <a className="text-jarvis-blue hover:underline" target="_blank" rel="noreferrer" href="https://developers.facebook.com/terms/">
            Facebook Platform Terms
          </a>{' '}
          and{' '}
          <a className="text-jarvis-blue hover:underline" target="_blank" rel="noreferrer" href="https://developers.facebook.com/devpolicy/">
            Developer Policies
          </a>. You can revoke our access at any time from inside Jarvis (Settings →
          Facebook Ads → Disconnect) or from Facebook directly at{' '}
          <a className="text-jarvis-blue hover:underline" target="_blank" rel="noreferrer" href="https://www.facebook.com/settings?tab=business_tools">
            Business Integrations
          </a>.
        </p>
      </Section>

      <Section id="google" heading="10. Google / Gmail-specific notes">
        <p>
          When you connect your Google account, Jarvis requests OAuth scopes that you explicitly
          approve on Google&rsquo;s consent screen. The scope set may include Gmail, Calendar,
          Drive, and identity scopes. The Gmail integration (when enabled) uses the{' '}
          <a className="text-jarvis-blue hover:underline" target="_blank" rel="noreferrer" href="https://developers.google.com/identity/protocols/oauth2/scopes#gmail">
            <code>https://www.googleapis.com/auth/gmail.modify</code>
          </a>{' '}
          scope. Our use of information received from Google APIs adheres to the{' '}
          <a className="text-jarvis-blue hover:underline" target="_blank" rel="noreferrer" href="https://developers.google.com/terms/api-services-user-data-policy">
            Google API Services User Data Policy
          </a>, including the{' '}
          <strong>Limited Use</strong> requirements.
        </p>
        <p>
          <strong>(a) What we access.</strong> When the Gmail integration is enabled, Jarvis reads
          your unread inbox messages (skipping the Promotions, Social, and Updates categories) at
          most once per day during the morning briefing run, plus on demand when you explicitly
          click <em>Run inbox triage now</em> from the Email tab. For each message we read the
          sender, recipient, subject, date, and body. We may also fetch the rest of the thread the
          message belongs to.
        </p>
        <p>
          <strong>(b) What we do with it.</strong> Each unread message is sent to a large-language-model
          provider (Anthropic, accessed via Microsoft Azure AI Foundry under contractual data-processor
          terms that prohibit training on the inputs) which classifies the message into one of three
          buckets: (1) <em>draft reply</em> — Jarvis creates a draft reply in your Gmail Drafts
          folder for you to review and explicitly approve before sending; (2) <em>flag for your
          review</em> — Jarvis surfaces the message in the in-product Email tab without taking
          action; or (3) <em>auto-handle</em> — for clear-noise messages such as receipts or
          newsletter notifications, Jarvis marks them read and applies the Gmail label{' '}
          <code>Jarvis/Processed</code> so they don&rsquo;t reappear. <strong>Jarvis never sends
          email on your behalf without your explicit per-draft approval.</strong>
        </p>
        <p>
          <strong>(c) What we store.</strong> We do <em>not</em> retain Gmail message bodies in our
          database. We persist only: the message <code>id</code> and <code>threadId</code> (for
          deduplication so we don&rsquo;t triage the same message twice), the AI-generated draft
          text we composed (so you can review it), and short factual snippets the AI extracted that
          you may want surfaced in the &ldquo;Jarvis learned today&rdquo; section. We do not retain
          raw message content, attachments, or recipient lists beyond what is described.
        </p>
        <p>
          <strong>(d) Limited Use compliance.</strong> Information obtained from Gmail APIs is used
          only to provide and improve the user-facing email-triage features described above. We
          do <strong>not</strong>: (i) use Gmail data to serve advertisements, (ii) sell or transfer
          Gmail data to third parties for advertising, credit-scoring, or other unrelated purposes,
          (iii) use Gmail data to train generalized or third-party AI/ML models, or (iv) allow
          humans to read your email except (a) with your explicit consent, (b) for security
          investigations, (c) to comply with applicable law, or (d) where the data has been
          aggregated and anonymized for internal operations.
        </p>
        <p>
          <strong>(e) Storage and encryption.</strong> Your Google OAuth refresh and access tokens
          are encrypted at rest using AES-256-GCM with a key held in Azure Key Vault, and the
          encrypted material is stored in Azure Cosmos DB on Microsoft Azure infrastructure
          located in the United States. The encryption key is never exposed to the browser or to
          any third-party processor.
        </p>
        <p>
          <strong>(f) Revoking access.</strong> You can disconnect Jarvis from your Google account
          at any time by clicking <em>Disconnect</em> on the Google card in Setup, which deletes
          our copy of your tokens immediately. You can also revoke our access directly from{' '}
          <a className="text-jarvis-blue hover:underline" target="_blank" rel="noreferrer" href="https://myaccount.google.com/permissions">
            Google Account → Security → Third-party apps with account access
          </a>.
        </p>
      </Section>

      <Section heading="11. Children">
        <p>
          The Service is intended for businesses and is not directed at children under the age of
          13 (or under 16 in the EU/UK). We do not knowingly collect personal information from
          children.
        </p>
      </Section>

      <Section heading="12. International users">
        <p>
          We operate the Service from the United States. By using the Service from outside the
          United States you understand that your data will be transferred to, stored in, and
          processed in the United States.
        </p>
      </Section>

      <Section heading="13. Changes to this policy">
        <p>
          We may update this Privacy Policy from time to time. Material changes will be
          communicated through the Service or by email. The &ldquo;Effective&rdquo; date at the
          top of this page reflects the most recent version.
        </p>
      </Section>

      <Section heading="14. Contact">
        <p>
          Questions, concerns, or requests? Email{' '}
          <a className="text-jarvis-blue hover:underline" href="mailto:kc@yourproject.ai">kc@yourproject.ai</a>{' '}
          — or write to ClearFinOps LLC, Lehi, Utah, USA.
        </p>
      </Section>
    </LegalLayout>
    </>
  )
}
